import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useLanguage } from '../contexts/LanguageContext';
import {
    Coffee, Wine, Zap, Footprints, Battery, Trophy, Beef, Droplets,
    Lightbulb, AlertTriangle, Search, X, ChevronRight, Sparkles, Heart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
    coffee: Coffee,
    wine: Wine,
    zap: Zap,
    footprints: Footprints,
    battery: Battery,
    trophy: Trophy,
    beef: Beef,
    droplets: Droplets,
    lightbulb: Lightbulb,
    heart: Heart,
    sparkles: Sparkles,
    alert: AlertTriangle,
};

const TYPE_STYLES = {
    warning: {
        bg: 'from-orange-500 to-red-500',
        border: 'border-orange-300 dark:border-orange-700',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200'
    },
    correlation: {
        bg: 'from-violet-500 to-indigo-500',
        border: 'border-violet-300 dark:border-violet-700',
        badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200'
    },
    celebration: {
        bg: 'from-green-500 to-emerald-500',
        border: 'border-green-300 dark:border-green-700',
        badge: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
    },
    info: {
        bg: 'from-cyan-500 to-blue-500',
        border: 'border-cyan-300 dark:border-cyan-700',
        badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200'
    },
    tip: {
        bg: 'from-amber-500 to-yellow-500',
        border: 'border-amber-300 dark:border-amber-700',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
    },
    prevention: {
        bg: 'from-blue-500 to-cyan-500',
        border: 'border-blue-300 dark:border-blue-700',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
    }
};

export function DailyInsights() {
    const { language } = useLanguage();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [dismissed, setDismissed] = useState(new Set());

    const t = {
        de: {
            title: 'Nanouks Erkenntnisse',
            subtitle: 'Was ich heute für dich entdeckt habe',
            noInsights: 'Alles im grünen Bereich! 🐺',
            dismiss: 'Verstanden',
            viewMore: 'Details',
            warning: 'Achtung',
            correlation: 'Entdeckung',
            celebration: 'Erfolg',
            info: 'Info',
            tip: 'Tipp',
            prevention: 'Vorbeugung'
        },
        en: {
            title: "Nanouk's Insights",
            subtitle: 'What I discovered for you today',
            noInsights: 'All good! 🐺',
            dismiss: 'Got it',
            viewMore: 'Details',
            warning: 'Warning',
            correlation: 'Discovery',
            celebration: 'Success',
            info: 'Info',
            tip: 'Tip',
            prevention: 'Prevention'
        }
    }[language];

    const loadInsights = async () => {
        try {
            const response = await axios.get(`${API}/api/insights/daily?language=${language}`);
            setInsights(response.data.insights || []);
        } catch (error) {
            console.error('Failed to load insights:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInsights();
    }, [language]);

    const handleDismiss = async (insightId) => {
        setDismissed(prev => new Set([...prev, insightId]));
        try {
            await axios.post(`${API}/api/insights/dismiss/${insightId}`, {});
        } catch (error) {
            console.error('Failed to dismiss insight:', error);
        }
    };

    const visibleInsights = insights.filter(i => !dismissed.has(i.id));

    if (loading) {
        return (
            <Card className="overflow-hidden">
                <CardContent className="p-4">
                    <Skeleton className="h-6 w-48 mb-4" />
                    <Skeleton className="h-24 w-full" />
                </CardContent>
            </Card>
        );
    }

    if (visibleInsights.length === 0) {
        return null; // Don't show anything if no insights
    }

    return (
        <Card className="overflow-hidden border-2 border-violet-200 dark:border-violet-800/50 bg-gradient-to-br from-violet-50/50 to-indigo-50/50 dark:from-violet-950/20 dark:to-indigo-950/20" data-testid="daily-insights">
            <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                            <Search className="w-5 h-5 text-foreground" />
                        </div>
                        <motion.div
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <Sparkles className="w-2.5 h-2.5 text-foreground" />
                        </motion.div>
                    </div>
                    <div>
                        <h3 className="font-semibold text-violet-900 dark:text-violet-100">{t.title}</h3>
                        <p className="text-xs text-violet-600 dark:text-violet-400">{t.subtitle}</p>
                    </div>
                </div>

                {/* Insights List */}
                <div className="space-y-3">
                    <AnimatePresence>
                        {visibleInsights.map((insight, index) => {
                            const IconComponent = ICON_MAP[insight.icon] || Lightbulb;
                            const styles = TYPE_STYLES[insight.type] || TYPE_STYLES.tip;
                            const isExpanded = expandedId === insight.id;

                            return (
                                <motion.div
                                    key={insight.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`relative rounded-xl border ${styles.border} bg-white dark:bg-card overflow-hidden`}
                                >
                                    {/* Gradient accent bar */}
                                    <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${styles.bg}`} />
                                    
                                    <div className="p-3 pl-4">
                                        {/* Header row */}
                                        <div className="flex items-start gap-3">
                                            {/* Nanouk image */}
                                            <motion.img
                                                src={insight.nanouk_image}
                                                alt="Nanouk"
                                                className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-border shadow-md"
                                                animate={insight.type === 'warning' ? { rotate: [0, -5, 5, 0] } : { y: [0, -2, 0] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            />
                                            
                                            <div className="flex-1 min-w-0">
                                                {/* Title and badge */}
                                                <div className="flex items-center gap-2 mb-1">
                                                    <IconComponent className={`w-4 h-4 flex-shrink-0`} />
                                                    <span className="font-medium text-sm truncate">{insight.title}</span>
                                                    <Badge className={`text-[10px] px-1.5 py-0 ${styles.badge}`}>
                                                        {t[insight.type] || insight.type}
                                                    </Badge>
                                                </div>
                                                
                                                {/* Message */}
                                                <p className={`text-sm text-muted-foreground ${isExpanded ? '' : 'line-clamp-2'}`}>
                                                    {insight.message}
                                                </p>
                                                
                                                {/* Action button */}
                                                {insight.action && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? 'auto' : 0 }}
                                                        className="mt-2 overflow-hidden"
                                                    >
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            className={`text-xs ${styles.badge}`}
                                                        >
                                                            {insight.action.text}
                                                            <ChevronRight className="w-3 h-3 ml-1" />
                                                        </Button>
                                                    </motion.div>
                                                )}
                                            </div>
                                            
                                            {/* Actions */}
                                            <div className="flex flex-col gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => setExpandedId(isExpanded ? null : insight.id)}
                                                >
                                                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                                                    onClick={() => handleDismiss(insight.id)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            </CardContent>
        </Card>
    );
}
